import { CustomerVehicleState } from '../model/enums/VehicleState';
import { InvoiceRowType } from '../model/enums/InvoiceRowType';
import { PurchaseInvoiceRow } from '../model/PurchaseInvoiceRow';

/**
 * True when a row carries everything needed to post its cost onto a vehicle and a customer.
 *
 * Shared by the server (automatic targeting, invoice state resolution) and the browser (the red
 * "Action Required" marker), so the two can never drift apart again: the UI used to check only
 * `!invoiceRowType`, which let a row whose type is `unspecified` look complete while the server
 * still considered it untargeted.
 */
export function isPurchaseInvoiceRowTargeted(row: PurchaseInvoiceRow): boolean {
    return Boolean(
        row.vehicleContractId &&
        row.vendorContractId &&
        row.invoiceRowType &&
        row.invoiceRowType !== InvoiceRowType.UNSPECIFIED,
    );
}

/** Vehicle states in which a row needs a look even though it is otherwise fully targeted. */
const ATTENTION_VEHICLE_STATES: string[] = [CustomerVehicleState.INACTIVE, CustomerVehicleState.REALIZATION];

/** Why a row needs a user's attention. */
export enum PurchaseInvoiceRowActionReason {
    /** The row is missing a vehicle contract, a vendor contract or a row type. */
    UNTARGETED = 'untargeted',
    /** The row is targeted onto a vehicle contract that is no longer in use. */
    VEHICLE_NOT_IN_USE = 'vehicle-not-in-use',
}

/**
 * Why a row needs a user's attention, or undefined when it does not.
 *
 * `vehicleState` is the state of the row's vehicle contract, or undefined when the row has no
 * vehicle contract - in which case the targeting check reports it instead.
 *
 * The vehicle state is checked first on purpose. A vehicle that is out of use is usually *why* no
 * vendor contract could be resolved, so a row failing for that reason looks like any other
 * untargeted row while its actual cause stays hidden.
 *
 * NOTE: `src/node/application/application_schema.ts` registers an equivalent SQL expression under
 * the `actionRequired` sort field so the server can order these rows first across pages. Any change
 * to what counts as needing attention must be mirrored there.
 */
export function getPurchaseInvoiceRowActionReason(
    row: PurchaseInvoiceRow,
    vehicleState: string | undefined,
): PurchaseInvoiceRowActionReason | undefined {
    if (ATTENTION_VEHICLE_STATES.includes(vehicleState ?? '')) {
        return PurchaseInvoiceRowActionReason.VEHICLE_NOT_IN_USE;
    }

    if (!isPurchaseInvoiceRowTargeted(row)) {
        return PurchaseInvoiceRowActionReason.UNTARGETED;
    }

    return undefined;
}

/** True when a row needs a user's attention, for any of the reasons above. */
export function isPurchaseInvoiceRowActionRequired(row: PurchaseInvoiceRow, vehicleState: string | undefined): boolean {
    return getPurchaseInvoiceRowActionReason(row, vehicleState) !== undefined;
}
